/**
 * Hotel booking page.
 *
 * Reads booking data written to sessionStorage by the hotel card/single page,
 * populates the summary and guest form, then runs:
 *   1. check-rate  (optional, non-fatal)
 *   2. book_hotel  (WP AJAX)
 *   3. alfabank_init_payment (WP AJAX) → redirect to payment form
 */

import { showToast } from "../utils/toast.js";

const SESSION_KEY = "dopline_booking_data";
const TRACE_KEY = "dopline_booking_trace_id";

function makeTraceId() {
    const rand = Math.random().toString(36).slice(2, 10);
    return `dl-${Date.now()}-${rand}`;
}

function ensureTraceId(hotel) {
    let traceId = hotel?.traceId || "";
    try {
        traceId = traceId || sessionStorage.getItem(TRACE_KEY) || "";
    } catch (_) {}
    if (!traceId) traceId = makeTraceId();
    if (hotel && !hotel.traceId) hotel.traceId = traceId;
    try {
        sessionStorage.setItem(TRACE_KEY, traceId);
        if (hotel) sessionStorage.setItem(SESSION_KEY, JSON.stringify(hotel));
    } catch (_) {}
    return traceId;
}

function qs(sel, root = document) {
    return root.querySelector(sel);
}

function formatPrice(amount) {
    const dc = window.doplineCurrency;
    if (dc && typeof dc.format === "function") return dc.format(amount);
    return new Intl.NumberFormat("ru-RU", {style: "currency", currency: "RUB", maximumFractionDigits: 0}).format(amount);
}

function formatDate(str) {
    if (!str) return "";
    // Already dd.mm.yyyy — return normalised (zero-pad if needed)
    const dotMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dotMatch) {
        return dotMatch[1].padStart(2, "0") + "." + dotMatch[2].padStart(2, "0") + "." + dotMatch[3];
    }
    const d = new Date(str.replace(" ", "T"));
    if (isNaN(d.getTime())) return str;
    const day   = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}.${month}.${d.getFullYear()}`;
}

// ------------------------------------------------------------------ helpers
function setMsg(el, text, isError = true) {
    if (!el) return;
    el.textContent = text || "";
    el.hidden = !text;
    el.classList.toggle("is-error", isError && !!text);
    el.classList.toggle("is-success", !isError && !!text);
}

function setMsgWithLink(el, text, linkText, linkHref) {
    if (!el) return;
    el.innerHTML = "";
    el.appendChild(document.createTextNode(text + " "));
    const a = document.createElement("a");
    a.href = linkHref;
    a.textContent = linkText;
    el.appendChild(a);
    el.hidden = false;
    el.classList.add("is-error");
    el.classList.remove("is-success");
}

function setBtnLoading(btn, loading) {
    if (!btn) return;
    btn.disabled = loading;
    btn.classList.toggle("is-loading", loading);
    if (!btn.dataset.origText) btn.dataset.origText = (btn.textContent || "").trim();
    if (!loading) btn.textContent = btn.dataset.origText;
}

function setStep(btn, progressEl, text) {
    if (btn) btn.textContent = text;
    if (!progressEl) return;
    progressEl.hidden = !text;
    const span = progressEl.querySelector("[data-booking-progress-text]");
    if (span) span.textContent = text || "";
}

function stopProgress(btn, progressEl) {
    setBtnLoading(btn, false);
    if (progressEl) progressEl.hidden = true;
}

/**
 * Returns a user-facing error message if check-rate blocked the booking, null otherwise.
 * rate_expired (#7)            — rate is gone, user must search again.
 * check_rate_unavailable (#1022) — supplier did not confirm rate; room is taken.
 */
function checkRateBlockMessage(crRes) {
    if (crRes?.code === "rate_expired") {
        return crRes.message || "К сожалению, этот тариф уже не актуален. Пожалуйста, выполните поиск заново.";
    }
    if (crRes?.code === "check_rate_unavailable") {
        return "К сожалению, этот номер уже занят, выберите другой.";
    }
    return null;
}

/**
 * Return a safe user-facing error string.
 * - validationMsg: the server message (already user-friendly for validation errors)
 * - ref: opaque ref code for support lookup
 * - fallback: what to show when we have no better text
 */
function safeErrorMsg(validationMsg, ref, fallback) {
    const refSuffix = ref ? ` (код: ${ref})` : "";
    // Server returns user-readable text only when there's no ref (validation error).
    // When there IS a ref, the message may contain technical API details — hide it.
    const displayMsg = (!ref && validationMsg) ? validationMsg : fallback;
    return displayMsg + refSuffix;
}

function post(ajaxUrl, nonce, action, payload = {}) {
    const body = new URLSearchParams({action, nonce});
    for (const [k, v] of Object.entries(payload)) {
        if (Array.isArray(v) || (v && typeof v === "object")) {
            body.set(k, JSON.stringify(v));
        } else {
            body.set(k, v == null ? "" : String(v));
        }
    }
    return fetch(ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"},
        body: body.toString(),
    }).then((r) => r.json());
}

// ------------------------------------------------------------------ guest rows
function guestRow(idx, type, adults) {
    const label = type === "chd" ? `Ребёнок ${idx - adults + 1}` : `Взрослый ${idx + 1}`;
    const ageField = type === "chd"
        ? `<div class="dopline-bpage__group">
               <label class="dl-label" for="g-age-${idx}">Возраст</label>
               <input class="dl-input" id="g-age-${idx}" type="number" min="0" max="17" name="guest_${idx}_age" value="7" required>
           </div>`
        : "";
    return `<div class="dopline-bpage__guest" data-guest="${idx}">
        <div class="dopline-bpage__guest-label">${label}</div>
        <div class="dopline-bpage__guest-grid">
            <div class="dopline-bpage__group">
                <label class="dl-label" for="g-fn-${idx}">Имя (латиницей)</label>
                <input class="dl-input" id="g-fn-${idx}" type="text" name="guest_${idx}_first_name" placeholder="Ivan" required>
            </div>
            <div class="dopline-bpage__group">
                <label class="dl-label" for="g-ln-${idx}">Фамилия (латиницей)</label>
                <input class="dl-input" id="g-ln-${idx}" type="text" name="guest_${idx}_last_name" placeholder="Petrov" required>
            </div>
            ${ageField}
        </div>
    </div>`;
}

function collectGuests(root, hotel) {
    const guests = [];
    let idx = 0;
    const latin = /^[A-Za-z\s.-]+$/;

    for (let i = 0; i < hotel.adults; i++, idx++) {
        const fn = qs(`[name="guest_${idx}_first_name"]`, root)?.value?.trim() || "";
        const ln = qs(`[name="guest_${idx}_last_name"]`, root)?.value?.trim() || "";
        if (!fn || !ln) return null;
        if (!latin.test(fn) || !latin.test(ln)) return "latin";
        guests.push({type: "adt", first_name: fn, last_name: ln});
    }

    for (let i = 0; i < hotel.children; i++, idx++) {
        const fn = qs(`[name="guest_${idx}_first_name"]`, root)?.value?.trim() || "";
        const ln = qs(`[name="guest_${idx}_last_name"]`, root)?.value?.trim() || "";
        const age = Number(qs(`[name="guest_${idx}_age"]`, root)?.value || 7);
        if (!fn || !ln) return null;
        if (!latin.test(fn) || !latin.test(ln)) return "latin";
        guests.push({type: "chd", first_name: fn, last_name: ln, age});
    }

    return guests;
}

// ------------------------------------------------------------------ summary
function buildSummary(hotel, root) {
    const el = qs("[data-booking-summary]", root);
    if (!el) return;

    const nights = hotel.nights || 1;
    const totalGuests = Math.max(1, hotel.adults + hotel.children);
    const cancelText = hotel.freeCancellationBefore
        ? `<div class="dopline-bpage__summary-row dopline-bpage__summary-row--ok">
               <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
               Бесплатная отмена до ${formatDate(hotel.freeCancellationBefore)}
           </div>`
        : (hotel.isNonRefundable
            ? `<div class="dopline-bpage__summary-row dopline-bpage__summary-row--warn">
                   <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                   Невозвратный тариф
               </div>`
            : "");

    const datesRow = (hotel.checkIn || hotel.checkOut)
        ? `<div class="dopline-bpage__summary-row">
               <svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>
               <span class="dopline-bpage__summary-dates">
                   <span>${formatDate(hotel.checkIn)}</span>
                   <span class="dopline-bpage__summary-dates-sep">—</span>
                   <span>${formatDate(hotel.checkOut)}</span>
               </span>
           </div>`
        : "";

    el.innerHTML = `
        <h3 class="dopline-bpage__summary-title">${hotel.title || "Отель"}</h3>
        ${hotel.roomName ? `<p class="dopline-bpage__summary-room">${hotel.roomName}</p>` : ""}
        <div class="dopline-bpage__summary-divider"></div>
        ${datesRow}
        <div class="dopline-bpage__summary-row">
            <svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
            ${totalGuests} гост${totalGuests === 1 ? "ь" : totalGuests < 5 ? "я" : "ей"}
        </div>
        <div class="dopline-bpage__summary-divider"></div>
        ${cancelText}
        <div class="dopline-bpage__summary-price-wrap">
            <div class="dopline-bpage__summary-price-label">Итого за ${nights} ${nights === 1 ? "ночь" : nights < 5 ? "ночи" : "ночей"}</div>
            <div class="dopline-bpage__summary-price">${formatPrice(hotel.price)}</div>
        </div>
    `;
}

// ------------------------------------------------------------------ card fields UX
function initCardFields(root) {
    const numInput = qs("[data-card-number]", root);
    const expInput = qs("[data-card-expiry]", root);
    const nameInput = qs("[data-card-name]", root);

    const numDisplay = qs("[data-card-number-display]", root);
    const expDisplay = qs("[data-card-expiry-display]", root);
    const holderDisplay = qs("[data-card-holder-display]", root);

    numInput?.addEventListener("input", () => {
        let v = numInput.value.replace(/\D/g, "").slice(0, 16);
        numInput.value = v.match(/.{1,4}/g)?.join(" ") || v;
        if (numDisplay) {
            const padded = (v + "0000000000000000").slice(0, 16);
            numDisplay.textContent = padded.match(/.{1,4}/g).join(" ");
        }
    });

    expInput?.addEventListener("input", () => {
        let v = expInput.value.replace(/\D/g, "").slice(0, 4);
        if (v.length >= 3) v = v.slice(0, 2) + "/" + v.slice(2);
        expInput.value = v;
        if (expDisplay) expDisplay.textContent = v || "MM/YY";
    });

    nameInput?.addEventListener("input", () => {
        if (holderDisplay) holderDisplay.textContent = nameInput.value.toUpperCase() || "CARDHOLDER NAME";
    });
}

// ------------------------------------------------------------------ check-rate
// Returns parsed JSON on both success and known API errors (e.g. rate_expired),
// or null on network failure. Callers should check crRes?.code === 'rate_expired'.
async function runCheckRate(restUrl, restNonce, hotel) {
    const traceId = ensureTraceId(hotel);
    try {
        const resp = await fetch(restUrl + "dopline/v1/myagent/hotel/check-rate", {
            method: "POST",
            credentials: "same-origin",
            headers: {"Content-Type": "application/json", "X-WP-Nonce": restNonce, "X-Dopline-Trace-Id": traceId},
            body: JSON.stringify({
                hs: hotel.hs,
                hotel_id: hotel.id,
                rate_id: hotel.rateId,
                price: hotel.basePrice || hotel.price,
                display_price: hotel.price,
                partner_affiliate_fee: hotel.partnerFee,
                agent_affiliate_fee: hotel.agentFee,
                lang: "ru",
                is_non_refundable: hotel.isNonRefundable,
                free_cancellation_before: hotel.freeCancellationBefore || "",
                cancellation_rules: hotel.cancellationRules || [],
                trace_id: traceId,
            }),
        });
        // Always parse JSON — even error responses (410 rate_expired, 500 server error)
        // carry a JSON body with code/message we need to act on.
        return resp.json().catch(() => null);
    } catch (_) {
        return null;
    }
}

// ------------------------------------------------------------------ init
export function initHotelBookingPage() {
    const root = qs("[data-hotel-booking-page]");
    if (!root) return;

    const ajaxUrl = root.dataset.ajaxUrl || "";
    const nonce = root.dataset.nonce || "";
    const restUrl = root.dataset.restUrl || "";
    const restNonce = root.dataset.restNonce || "";
    const dashboardUrl = root.dataset.dashboardUrl || "/dashboard/";
    const successUrl = root.dataset.successUrl || dashboardUrl;
    const errorUrl = root.dataset.errorUrl || "";

    // Holds the price-updated hotel object when check-rate returns a new price.
    // The user must click submit again to confirm the new price.
    let _priceUpdatedHotel = null;

    const layoutEl = qs("[data-booking-layout]", root);
    const emptyEl = qs("[data-booking-empty]", root);
    const successEl = qs("[data-booking-success]", root);
    const successTextEl = qs("[data-booking-success-text]", root);
    const msgEl = qs("[data-booking-message]", root);
    const progressEl = qs("[data-booking-progress]", root);
    const guestsWrap = qs("[data-booking-guests]", root);
    const submitBtn = qs("[data-booking-submit]", root);

    // Read hotel data from sessionStorage
    let hotel = null;
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (raw) hotel = JSON.parse(raw);
    } catch (_) {}

    if (!hotel || !hotel.id || !hotel.hs || !hotel.rateId) {
        if (emptyEl) emptyEl.hidden = false;
        return;
    }
    const traceId = ensureTraceId(hotel);

    // Show layout
    if (layoutEl) layoutEl.hidden = false;

    // Build summary
    buildSummary(hotel, root);

    // Start check-rate on page load. Store the promise so the submit handler can
    // await it — check-rate can take 10-20 s, and the user may click submit while
    // it is still in flight. We must not send /hotel/book before check-rate resolves.
    const checkRatePromise = runCheckRate(restUrl, restNonce, hotel);
    checkRatePromise.then((crRes) => {
        const blockMsg = checkRateBlockMessage(crRes);
        if (blockMsg) {
            if (submitBtn) submitBtn.disabled = true;
            setMsgWithLink(msgEl, blockMsg, "← Вернуться к поиску", "javascript:history.back()");
        }
    });

    // Build guest rows
    if (guestsWrap) {
        let rows = "";
        let idx = 0;
        for (let i = 0; i < hotel.adults; i++, idx++) rows += guestRow(idx, "adt", hotel.adults);
        for (let i = 0; i < hotel.children; i++, idx++) rows += guestRow(idx, "chd", hotel.adults);
        guestsWrap.innerHTML = rows;
    }


    // Submit
    submitBtn?.addEventListener("click", async () => {
        setMsg(msgEl, "");

        // Validate contact
        const emailInput = qs('[name="client_email"]', root);
        const phoneInput = qs('[name="client_phone"]', root);
        const email = emailInput?.value?.trim() || "";
        const phone = phoneInput?.value?.trim() || "";

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setMsg(msgEl, "Введите корректный email.");
            emailInput?.focus();
            return;
        }
        if (!phone) {
            setMsg(msgEl, "Введите номер телефона.");
            phoneInput?.focus();
            return;
        }

        // Validate guests
        const guestsResult = collectGuests(root, hotel);
        if (guestsResult === null) {
            setMsg(msgEl, "Заполните имя и фамилию всех гостей.");
            return;
        }
        if (guestsResult === "latin") {
            setMsg(msgEl, "Имена гостей должны быть введены латинскими буквами (например: Ivan Petrov).");
            return;
        }
        const guests = guestsResult;

        setBtnLoading(submitBtn, true);

        // Await the page-load check-rate before booking. If it is still in flight
        // (user clicked submit faster than the ~15 s API response), we wait here.
        // null return = network error → proceed; any blockMsg → block.
        setStep(submitBtn, progressEl, "Проверяем тариф…");
        const crRes = await checkRatePromise;
        const blockMsg = checkRateBlockMessage(crRes);
        if (blockMsg) {
            stopProgress(submitBtn, progressEl);
            if (submitBtn) submitBtn.disabled = true;
            setMsgWithLink(msgEl, blockMsg, "← Вернуться к поиску", "javascript:history.back()");
            return;
        }

        // If check-rate detected a price change, compare against the price already
        // shown on our page, not against API "old.price" which can be stale.
        let currentHotel = _priceUpdatedHotel || hotel;
        _priceUpdatedHotel = null;
        if (crRes?.success && crRes?.data?.check_price_changes?.new?.price != null) {
            const newPrice = Number(crRes.data.check_price_changes.new.price);
            const currentPrice = Number(currentHotel.basePrice ?? currentHotel.price ?? 0);
            if (Number.isFinite(newPrice) && newPrice > 0 && Math.round(newPrice) !== Math.round(currentPrice)) {
                const newPartnerFee = Number(crRes.data.check_price_changes.new.partnerAffiliateFee);
                currentHotel = {
                    ...currentHotel,
                    price: newPrice,
                    basePrice: newPrice,
                    partnerFee: Number.isFinite(newPartnerFee) ? newPartnerFee : currentHotel.partnerFee,
                    traceId,
                };
                hotel = currentHotel;
                try {
                    sessionStorage.setItem(SESSION_KEY, JSON.stringify(currentHotel));
                } catch (_) {}
                buildSummary(currentHotel, root);
                setMsg(msgEl,
                    `Цена изменилась: было ${formatPrice(currentPrice)}, стало ${formatPrice(newPrice)}. Бронируем по актуальной цене.`,
                    false
                );
            }
        }

        // Build book payload (flat URLSearchParams format)
        const bookPayload = {
            hotel_id: currentHotel.id,
            hs: currentHotel.hs,
            rate_id: currentHotel.rateId,
            price: currentHotel.basePrice ?? currentHotel.price,
            is_non_refundable: currentHotel.isNonRefundable,
            partner_affiliate_fee: currentHotel.partnerFee,
            agent_affiliate_fee: currentHotel.agentFee,
            free_cancellation_before: currentHotel.freeCancellationBefore || "",
            client_email: email,
            client_phone: phone,
            trace_id: traceId,
        };

        guests.forEach((g, i) => {
            bookPayload[`guests[${i}][first_name]`] = g.first_name;
            bookPayload[`guests[${i}][last_name]`] = g.last_name;
            bookPayload[`guests[${i}][type]`] = g.type;
            if (g.type === "chd") bookPayload[`guests[${i}][age]`] = String(g.age);
        });

        (currentHotel.cancellationRules || []).forEach((rule, i) => {
            bookPayload[`cancellation_policy_rules[${i}][isPossible]`] = rule.isPossible ? "1" : "0";
            bookPayload[`cancellation_policy_rules[${i}][amount]`] = String(rule.amount || 0);
            bookPayload[`cancellation_policy_rules[${i}][UTCDateFrom]`] = rule.UTCDateFrom || "";
            bookPayload[`cancellation_policy_rules[${i}][UTCDateTo]`] = rule.UTCDateTo || "";
        });

        setStep(submitBtn, progressEl, "Создаём бронирование…");
        let billingNumber = null;
        try {
            const bookRes = await post(ajaxUrl, nonce, "dopline_auth_book_hotel", bookPayload);
            if (!bookRes?.success) {
                const code = bookRes?.data?.code || "";
                const ref  = bookRes?.data?.ref  || "";

                if (code === "rate_expired") {
                    // Rate is gone — disable button permanently, no point retrying.
                    stopProgress(submitBtn, progressEl);
                    if (submitBtn) submitBtn.disabled = true;
                    setMsgWithLink(
                        msgEl,
                        bookRes.data.message,
                        "← Вернуться к поиску",
                        "javascript:history.back()"
                    );
                    return;
                }

                stopProgress(submitBtn, progressEl);

                if (code === "network_error") {
                    setMsgWithLink(
                        msgEl,
                        bookRes.data.message,
                        "Мои бронирования →",
                        dashboardUrl
                    );
                    return;
                }

                if (code === "price_changed") {
                    const ch = bookRes.data.check_price_changes;
                    const newDisplayPrice = Number(ch?.new?.price ?? 0);
                    if (Number.isFinite(newDisplayPrice) && newDisplayPrice > 0) {
                        const updatedHotel = {...currentHotel, price: newDisplayPrice, basePrice: newDisplayPrice};
                        buildSummary(updatedHotel, root);
                        _priceUpdatedHotel = updatedHotel;
                        setMsg(msgEl,
                            `Цена изменилась: было ${formatPrice(currentHotel.price)}, стало ${formatPrice(newDisplayPrice)}. Нажмите «Оплатить и забронировать» ещё раз для подтверждения.`,
                            false
                        );
                    } else {
                        setMsg(msgEl, "Цена на тариф изменилась. Нажмите «Оплатить и забронировать» ещё раз для подтверждения.", false);
                    }
                    return;
                }

                if (ref && errorUrl) {
                    window.location.href = `${errorUrl}?ref=${ref}`;
                    return;
                }
                setMsg(msgEl, bookRes?.data?.message || "Не удалось создать бронирование. Попробуйте ещё раз.");
                return;
            }
            billingNumber = bookRes.data?.order?.billing_number || null;
        } catch (err) {
            console.error("[book-hotel] unexpected error:", err);
            // SyntaxError = сервер вернул не-JSON (таймаут nginx, fatal PHP error).
            // Бронирование могло создаться на стороне MA — кнопку НЕ разблокируем,
            // чтобы предотвратить повторное бронирование.
            const isParseErr = err instanceof SyntaxError;
            if (isParseErr) {
                if (progressEl) progressEl.hidden = true;
                if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.textContent = submitBtn.dataset.origText || submitBtn.textContent;
                }
                setMsgWithLink(
                    msgEl,
                    "Сервер вернул неожиданный ответ. Проверьте раздел «Мои бронирования» — если заказ там появился, всё прошло успешно. Если нет — обновите страницу и попробуйте снова.",
                    "Мои бронирования →",
                    dashboardUrl
                );
            } else {
                stopProgress(submitBtn, progressEl);
                showToast("Ошибка соединения. Проверьте интернет и попробуйте ещё раз.", "error");
            }
            return;
        }

        if (!billingNumber) {
            stopProgress(submitBtn, progressEl);
            showToast("Не удалось получить номер заказа. Пожалуйста, свяжитесь с поддержкой.", "error");
            return;
        }

        // Step 2.5: check-price + apply-changes before payment (mandatory per API docs).
        // The booking may have received updated conditions from the supplier between
        // /hotel/book returning and payment being initiated. Apply any changes automatically
        // so the booking state is consistent before the Alfa-Bank redirect.
        setStep(submitBtn, progressEl, "Проверяем актуальность тарифа…");
        try {
            const cpRes = await fetch(restUrl + "dopline/v1/myagent/hotel/check-price", {
                method: "POST",
                credentials: "same-origin",
                headers: {"Content-Type": "application/json", "X-WP-Nonce": restNonce, "X-Dopline-Trace-Id": traceId},
                body: JSON.stringify({billing_number: billingNumber, trace_id: traceId}),
            });
            const cpData = await cpRes.json().catch(() => null);
            if (cpData?.success && Array.isArray(cpData.data) && cpData.data.length > 0) {
                const hasAnyChange = cpData.data.some((entry) => {
                    const pc = entry?.check_price_changes;
                    const cc = entry?.cancellation_policy_rules_changes;
                    const fc = entry?.free_cancellation_before_changes;
                    return (pc?.new && pc?.old) || (Array.isArray(cc?.new) && cc.new.length > 0) || (fc?.new && fc?.old);
                });
                if (hasAnyChange) {
                    await fetch(restUrl + "dopline/v1/myagent/hotel/apply-changes", {
                        method: "POST",
                        credentials: "same-origin",
                        headers: {"Content-Type": "application/json", "X-WP-Nonce": restNonce, "X-Dopline-Trace-Id": traceId},
                        body: JSON.stringify({billing_number: billingNumber, trace_id: traceId}),
                    }).catch(() => null);
                }
            }
        } catch (_) {
            // Non-fatal: proceed to payment even if check-price fails.
        }

        // Step 3: register payment in Alfa-Bank and redirect to payment form
        setStep(submitBtn, progressEl, "Переходим к оплате…");
        try {
            const abRes = await post(ajaxUrl, nonce, "dopline_alfabank_init_payment", {billing_number: String(billingNumber)});
            if (!abRes?.success) {
                const ref = abRes?.data?.ref || "";
                stopProgress(submitBtn, progressEl);
                if (errorUrl) {
                    window.location.href = `${errorUrl}?ref=${ref}&billing_number=${billingNumber}`;
                    return;
                }
                showToast(
                    safeErrorMsg(null, ref, "Бронирование создано, но перейти к оплате не удалось. Обратитесь в поддержку с номером заказа: " + billingNumber),
                    "error"
                );
                return;
            }

            const formUrl = abRes.data?.formUrl;
            if (!formUrl) {
                stopProgress(submitBtn, progressEl);
                showToast("Бронирование создано, но ссылка на оплату не получена. Обратитесь в поддержку с номером заказа: " + billingNumber, "error");
                return;
            }

            // Redirect to Alfa-Bank payment page.
            sessionStorage.removeItem(SESSION_KEY);
            window.location.href = formUrl;

        } catch (_) {
            stopProgress(submitBtn, progressEl);
            showToast("Ошибка соединения при переходе к оплате. Бронирование создано — обратитесь в поддержку с номером заказа: " + billingNumber, "error");
        }
    });
}
